import { Undo2 } from "lucide-react";
import type { ComponentPropsWithRef } from "react";
import { MY_ROW_TINT } from "@/shared/lib/rowTint";
import { cn } from "@/shared/lib/utils";
import { classColorStyle } from "@/shared/lib/wowClass";
import type { PlayerChange } from "../setup.overrides";
import type { AbsentPlayer, SetupPlayer } from "../setup.types";
import { PlayerContextMenu } from "./PlayerContextMenu";
import { PlayerLabel } from "./PlayerLabel";

// Shared "Ej med" panel rendered below both the Setup board and the Boss-för-boss
// matrix. Bench players are interactive (restore into the setup via the ↩ button or
// the right-click menu); absent players are read-only with their reason.
export function UnavailablePanel({
  players,
  absent,
  editable,
  onChange,
  onDeleteStandIn,
  onEditStandIn,
}: {
  players: SetupPlayer[];
  absent: AbsentPlayer[];
  editable: boolean;
  onChange: (player: SetupPlayer, change: PlayerChange) => void;
  onDeleteStandIn: (id: string) => void;
  onEditStandIn: (player: SetupPlayer) => void;
}) {
  const benched = players.filter(
    (player) =>
      player.benched &&
      (player.kind === "member" || player.kind === "stand_in"),
  );

  // Hide empty sections; hide the whole box when there's nothing to show.
  if (benched.length === 0 && absent.length === 0) return null;

  return (
    <section className="w-fit min-w-52 max-w-full overflow-hidden rounded-lg border border-border">
      <header className="bg-muted px-3 py-1.5 text-xs font-semibold">
        Ej med
      </header>
      <div className="space-y-3 p-3">
        {benched.length > 0 && (
          <div className="space-y-1.5">
            <SubHeading label="Bänk" count={benched.length} />
            <div className="flex flex-wrap gap-1.5">
              {benched.map((player) => {
                const key = `${player.kind}:${player.id}`;
                return editable ? (
                  <PlayerContextMenu
                    key={key}
                    player={player}
                    onChange={(change) => onChange(player, change)}
                    onDeleteStandIn={onDeleteStandIn}
                    onEditStandIn={onEditStandIn}
                  >
                    <BenchCard
                      player={player}
                      onRestore={() => onChange(player, { benched: false })}
                    />
                  </PlayerContextMenu>
                ) : (
                  <BenchCard key={key} player={player} />
                );
              })}
            </div>
          </div>
        )}

        {benched.length > 0 && absent.length > 0 && (
          <div className="h-px bg-border" />
        )}

        {absent.length > 0 && (
          <div className="space-y-1.5">
            <SubHeading label="Frånvarande" count={absent.length} />
            <div className="flex flex-wrap gap-1.5">
              {absent.map((player) => (
                <AbsentCard key={player.userId} player={player} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function SubHeading({ label, count }: { label: string; count: number }) {
  return (
    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {label} · {count}
    </p>
  );
}

// Forwards ref + props to its root so it can be the direct child of
// ContextMenuTrigger's Slot (right-click menu) when editable.
function BenchCard({
  player,
  onRestore,
  className,
  ref,
  ...rest
}: ComponentPropsWithRef<"div"> & {
  player: SetupPlayer;
  onRestore?: () => void;
}) {
  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm",
        player.isMe ? MY_ROW_TINT : "bg-card",
        className,
      )}
      {...rest}
    >
      <PlayerLabel player={player} badgeClassName="px-1.5 py-0.5 text-xs" />
      {onRestore && (
        <button
          type="button"
          aria-label="Ta in i setupen"
          title="Ta in i setupen"
          onClick={onRestore}
          className="ml-1 inline-flex size-5 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground hover:bg-secondary"
        >
          <Undo2 className="size-3.5" />
        </button>
      )}
    </div>
  );
}

function AbsentCard({ player }: { player: AbsentPlayer }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md border border-dashed border-border px-2.5 py-1.5 text-sm opacity-60",
        player.isMe && MY_ROW_TINT,
      )}
    >
      <span className="font-medium" style={classColorStyle(player.wowClass)}>
        {player.name}
      </span>
      <span className="text-xs text-muted-foreground">{player.reason}</span>
    </div>
  );
}
